import React, { useEffect, useState } from 'react';
import App from './App';
import './App.css';
import { Container, Spinner, Modal } from 'react-bootstrap';
import { BrowserRouter as Router, Switch, Route, Redirect } from 'react-router-dom';
import Login from './Auth/Login';
import ResetPassword from './Auth/ResetPassword';
import ForcePasswordChange from './Auth/ForcePasswordChange';
import RestAuthManager from './Auth';
import AdvancedConfigApp from './AdvancedConfig/AdvancedConfigApp';
import { isReadOnlyRole } from './Auth/permissions';

const AuthStateApp = () => {
  const [user, setUser] = useState(null);
  const [config, setConfig] = useState(null);
  const [connection, setConnection] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [error, setError] = useState(null);

  const getAppConfig = async () => {
    let appConfig = {};
    try {
      let response = await fetch('/config', {
        method: 'GET',
      });
      let body = await response.json()
      appConfig = body;
      if (body.error) {
        setError(body.error);
      }
      return appConfig;
    } catch (err) {
      // console.log('Config server could not deliver settings, trying fallback');
      console.log(err);
      
      try {
        const url = ((typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL) || '') + '/api-config.json';
        const response = await fetch(url);
        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) {
          return {};
        }
        const parsed = await response.json();
        return typeof parsed === 'object' && parsed !== null ? parsed : {};
      } catch (fallbackErr) {
        // console.log('Fallback config also failed');
        console.log(fallbackErr);
        setError('Failed to load configuration');
        return {};
      }
    }
  }

  useEffect(() => {
    const init = async () => {
      if (!config)  {
        const appConfig = await getAppConfig();

        await RestAuthManager.setupApi(appConfig);
        const userData = await RestAuthManager.loadCookieAndValidate();

        setConfig(appConfig);
        if (userData) { handleLogin(userData); }
        
      } 
    }
    init();
  }, []);

  // A signed-in page restored from the browser's back/forward cache comes back with this
  // component's state intact — `user` still set — even though logout cleared the cookie. The
  // page then renders as though signed in: browser Back after logging out walked straight
  // into /advanced-config (C2D-1198). Nothing fetches (every request is unauthenticated
  // without the cookie), so what is exposed is a stale render rather than live data, but it
  // reads as a session that never ended.
  //
  // `pageshow` is the one event a bfcache restore fires; `visibilitychange` covers logging
  // out in another tab and coming back to this one.
  useEffect(() => {
    const clearIfSignedOut = () => {
      if (!RestAuthManager.hasSessionCookie()) {
        setUser(null);
        setIsAdmin(false);
        setIsReadOnly(false);
        setMustChangePassword(false);
      }
    };
    const onPageShow = () => clearIfSignedOut();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') clearIfSignedOut();
    };
    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const handleLogin = async (userData) => {
    // console.log('USER userdata', userData)
    try {
      await RestAuthManager.setJwtToken(userData.token);
      await RestAuthManager.setUser(userData);
      setUser(userData);
      setMustChangePassword(!!userData.passwordChangeRequired);
      if (userData.role === 'ADMIN') {
        setIsAdmin(true);
      }
      setIsReadOnly(isReadOnlyRole(userData.role));
    } catch (err) {
      console.log('Error setting user', err)
    }
  };

  return (
    config === null ? (
      <Container>
        <div className='spinner-container'>
          <Spinner animation='grow' variant='primary' />
          {error && <ErrorMessage error={error} />}
        </div>
      </Container>
    ) : (
      <div>
        {user ? (
          mustChangePassword ? (
            <ForcePasswordChange onComplete={() => setMustChangePassword(false)} />
          ) : (
            <Container fluid className='m-0 p-0' data-testid="app-container">
              <Router>
                <React.StrictMode>
                  {/*
                    Hidden "advanced settings" app. Mounted here, ABOVE <App>, so it renders its
                    own full-page shell with NONE of the main app's Navigation chrome. Reachable
                    only by typing the URL /advanced-config — there is intentionally no link to it
                    anywhere in <App> or <Navigation>. Auth is shared: this branch only renders
                    when `user` is set (same login gate as the main app).
                  */}
                  <Switch>
                    <Route path='/advanced-config'>
                      <AdvancedConfigApp
                        config={config}
                        user={user}
                        isAdmin={isAdmin}
                      />
                    </Route>
                    {/* A signed-in user clicking a password-reset/invite email link
                        shouldn't 404 — send them to the app. */}
                    <Route path='/reset-password'>
                      <Redirect to='/' />
                    </Route>
                    <Route path='/'>
                      <App
                        config={config}
                        connection={connection}
                        user={user}
                        isAdmin={isAdmin}
                        isReadOnly={isReadOnly}
                      />
                    </Route>
                  </Switch>
                </React.StrictMode>
              </Router>
            </Container>
          )
        ) : (
              <Router>
                <Switch>
                  <Route path='/reset-password'>
                    <ResetPassword />
                  </Route>
                  <Route path='/'>
                    <Login onLogin={handleLogin} config={config} />
                  </Route>
                </Switch>
              </Router>
        )}
      </div>
    )
  )
}

const ErrorMessage = ({ error }) => {
  return (
    <Modal show={true} centered>
      <Modal.Header>
        <Modal.Title>Could not retrieve configuration</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>Please verify IAM permissions.</p>
        <p>{error}</p>
      </Modal.Body>
    </Modal>
  )
}

export default AuthStateApp;